import type { RagInputType, ReferenceConfig, ToolInputType } from './aia-types'
/**
 * Helper function to transform ToolInputType into the complex inputs JSON format
 * required by ServiceNow AI Agent tools.
 */

/**
 * Generates tableColumn metadata from a field name and explicit type/referenceConfig.
 * Does NOT auto-generate labels or infer types - uses field name as-is and requires explicit type.
 *
 * @param fieldName - The name of the field
 * @param fieldType - Explicit field type (defaults to 'string' if not provided)
 * @param referenceConfig - Optional reference configuration for reference-type fields
 * @returns The tableColumn object with metadata
 */

function generateFieldMetadata(fieldName: string, fieldType?: string, referenceConfig?: ReferenceConfig) {
    // Use field name as-is for label (no transformation)
    const label = fieldName

    // Use provided type or default to 'string' (no inference from field name)
    const type = fieldType || 'string'

    // biome-ignore lint/suspicious/noExplicitAny: Dynamic JSON structure for ServiceNow metadata
    const metadata: any = {
        id: fieldName,
        name: fieldName,
        label: label,
        type: type,
        sublabel: fieldName,
        excludedOperators: [],
        dictionaryAttributes: [],
    }

    // Add reference configuration if provided
    if (referenceConfig && type === 'reference') {
        metadata.referenceTable = referenceConfig.table
        metadata.referenceTableDefaultField = referenceConfig.field || null
        metadata.referenceKey = null
    }

    return metadata
}

/**
 * Transforms a simplified ToolInputType configuration into the complex inputs JSON array
 * that ServiceNow expects for AI Agent tools.
 *
 * @param toolInput - The simplified tool input configuration
 * @returns An array of input objects in the format expected by ServiceNow
 *
 * @example
 * ```typescript
 * const toolInput: ToolInputType = {
 *   operationName: 'create',
 *   table: 'sn_ba_tweet_collector',
 *   inputFields: [
 *     { name: 'content', description: 'Tweet content', mandatory: true, mappedToColumn: 'content', type: 'string' },
 *     { name: 'author', description: 'Tweet author', mandatory: true, mappedToColumn: 'author' }
 *   ],
 *   returnFields: [{ name: 'sys_id' }, { name: 'content' }],
 *   limit: 10,
 *   orderBy: 'sys_created_on',
 *   sortType: 'z_to_a'
 * }
 * ```
 */
export function generateToolInputs(toolInput: ToolInputType | Record<string, unknown>) {
    // Type guard: ensure toolInput has the required ToolInputType structure
    if (!isToolInputType(toolInput)) {
        return []
    }

    const inputs: unknown[] = []

    // Add all input fields with empty values
    for (const field of toolInput.inputFields) {
        inputs.push({
            name: field.name,
            value: field.value || '',
            description: field.description,
            mandatory: field.mandatory,
            invalidMessage: field.invalidMessage ?? null,
        })
    }

    // Normalize table to TableReference format
    const tableRef =
        typeof toolInput.table === 'string'
            ? { value: toolInput.table, displayValue: toolInput.table }
            : toolInput.table

    // Build the crudInputs object
    const crudInputs: any = {
        operation: toolInput.operationName,
        table: tableRef,
    }

    // Add query if present (for update, lookup, delete operations)
    if (toolInput.queryCondition) {
        crudInputs.query = toolInput.queryCondition
    }

    // Add field values for create/update operations using mappedToColumn in inputFields
    const fieldsWithMappings = toolInput.inputFields.filter((field) => field.mappedToColumn)

    if (fieldsWithMappings.length > 0) {
        crudInputs.fieldValues = fieldsWithMappings.map((field) => {
            const mappedColumn = field.mappedToColumn
            if (!mappedColumn) {
                return null
            }
            const column = generateFieldMetadata(mappedColumn, field.type, field.referenceConfig)
            // biome-ignore lint/suspicious/noExplicitAny: Dynamic JSON structure for field definition
            const fieldDef: any = {
                id: column.id,
                sublabel: column.sublabel || column.id,
                excludedOperators: column.excludedOperators || [],
                label: column.label,
                type: column.type,
                dictionaryAttributes: column.dictionaryAttributes || [],
                name: column.name,
            }

            // Add reference fields if present
            if (column.referenceTable) {
                fieldDef.referenceTable = column.referenceTable
                fieldDef.referenceTableDefaultField = column.referenceTableDefaultField
                fieldDef.referenceKey = column.referenceKey
            }

            return {
                field: fieldDef,
                value: {
                    fieldValue: `{{${field.name}}}`, // Use field.name as the variable name
                },
            }
        })
    }

    // Add return fields for lookup/create/update operations
    if (toolInput.returnFields && toolInput.returnFields.length > 0) {
        // Transform returnFields to include all required metadata
        crudInputs.returnFields = toolInput.returnFields.map((field) => {
            const metadata = generateFieldMetadata(field.name, field.type)
            return {
                excludedOperators: metadata.excludedOperators || [],
                extendedOperators: null,
                name: field.name,
                label: metadata.label,
                type: field.type || metadata.type,
                referenceTable: null,
                referenceTableDefaultField: null,
                referenceKey: null,
                dictionaryAttributes: metadata.dictionaryAttributes || [],
                id: field.name,
                sublabel: field.name,
            }
        })
    }

    // Add limit/orderBy/sortType for operations
    if (toolInput.limit !== undefined) {
        crudInputs.limit = String(toolInput.limit)
    }

    if (toolInput.orderBy !== undefined) {
        crudInputs.orderBy = toolInput.orderBy
    }

    if (toolInput.sortType !== undefined) {
        crudInputs.sortType = toolInput.sortType
    }

    // Add the crudInputs as the last input field
    inputs.push({
        name: 'crudInputs',
        value: crudInputs,
        description: `${toolInput.operationName.charAt(0).toUpperCase() + toolInput.operationName.slice(1)} operation configuration`,
        mandatory: true,
        invalidMessage: null,
    })

    return inputs
}

/**
 * Type guard to check if inputs is a ToolInputType
 */
export function isToolInputType(inputs: unknown): inputs is ToolInputType {
    return (
        inputs !== null &&
        inputs !== undefined &&
        typeof inputs === 'object' &&
        !Array.isArray(inputs) &&
        'inputFields' in inputs &&
        'operationName' in inputs &&
        'table' in inputs
    )
}

/**
 * Generates inputs array for RAG (Search Retrieval) tools
 * @param ragInput - The RAG input configuration
 * @returns Array of input objects formatted for ServiceNow
 */
export function generateRagInputs(ragInput: RagInputType): unknown[] {
    const inputs: unknown[] = []

    const { searchType } = ragInput

    // Search type
    inputs.push({
        name: 'search_type',
        description: 'Search type',
        value: searchType.type,
    })

    // Search profile — supports ValueLabelType { value, label } or Record data { name, label }
    const profileObj = ragInput.searchProfile as Record<string, string>
    const profileValue = profileObj.value ?? profileObj.name
    const profileLabel = profileObj.label

    inputs.push({
        name: 'query',
        description: `Search query for retrieving relevant information from ${profileLabel}`,
    })

    inputs.push({
        name: 'search_profile',
        value: profileValue,
        label: profileLabel,
    })

    // Sources (tables to search) - optional
    if (ragInput.sources && ragInput.sources.length > 0) {
        const sourceValues = ragInput.sources.map((source) => source.value)
        const sourceLabels = ragInput.sources.map((source) => source.label)

        inputs.push({
            name: 'sources',
            value: sourceValues,
            label: sourceLabels,
        })
    }

    // Fields to return (optional, applies to all search types)
    if (ragInput.fields && ragInput.fields.length > 0) {
        const fieldValues = ragInput.fields.map((field) => field.value)
        const fieldLabels = ragInput.fields.map((field) => field.label)

        inputs.push({
            name: 'fields',
            value: fieldValues,
            label: fieldLabels,
        })
    }

    // Search results limit (defaults to 10, applies to all search types)
    inputs.push({
        name: 'search_results_limit',
        value: String(ragInput.searchResultsLimit ?? 10),
    })

    // Semantic index names and document match threshold only apply to semantic/hybrid types
    if (searchType.type !== 'keyword') {
        // Semantic index names (directly from searchType)
        if (searchType.semanticIndexes && searchType.semanticIndexes.length > 0) {
            const indexesArray = searchType.semanticIndexes
            // Extract values - semantic indexes are field names from ais_dictionary
            // biome-ignore lint/suspicious/noExplicitAny: Shape getValue() may return various types
            const indexValues = indexesArray.map((index: any) => {
                if (typeof index === 'string') {
                    return index
                }
                if (index && typeof index === 'object' && 'value' in index) {
                    return index.value
                }
                return String(index)
            })

            // Extract labels from user input
            // biome-ignore lint/suspicious/noExplicitAny: Shape getValue() may return various types
            const indexLabels = indexesArray.map((index: any) => {
                if (typeof index === 'string') {
                    return index
                }
                if (index && typeof index === 'object' && 'label' in index) {
                    return index.label
                }
                return String(index)
            })

            inputs.push({
                name: 'semantic_index_names',
                value: indexValues,
                label: indexLabels,
            })
        }

        // Document match threshold (from searchType, defaults to 0)
        // Validation for 0-1 range happens at build time via diagnostics
        inputs.push({
            name: 'document_match_threshold',
            value: searchType.documentMatchThreshold ?? 0,
        })
    } else if (searchType.type === 'keyword') {
        inputs.push({
            name: 'document_match_threshold',
            value: 0,
        })
    }

    return inputs
}
